import type { ApiResponse } from '@bandos/shared'
import { TokenManager } from '../auth/tokenManager'

/** Auth header record returned on success */
interface AuthHeaders {
  Authorization: string
  [key: string]: string
}

/** Result of an authentication check — either headers or an auth-required error */
type AuthResult<T> =
  | { headers: AuthHeaders; error?: undefined }
  | { headers?: undefined; error: ApiResponse<T> }

/**
 * Retrieve auth headers, returning a typed auth-required error when missing.
 * Replaces the 8x repeated auth-check block across AI service methods.
 */
export async function getAuthenticatedHeaders<T>(): Promise<AuthResult<T>> {
  const headers = await TokenManager.getAuthHeaders()

  if (!headers.Authorization) {
    return {
      error: {
        data: null,
        error: {
          message: 'Authentication required. Please log in.',
          code: 'AUTH_REQUIRED',
        },
      },
    }
  }

  return { headers: headers as AuthHeaders }
}

/**
 * Transform a raw backend response that follows the `{ success, message?, ...data }` pattern
 * into a clean `ApiResponse<TOut>`.
 *
 * Replaces the 6x repeated 3-branch if/else transform:
 *   1. response.error → pass through
 *   2. success + data present → extract with `extractData`
 *   3. !success or missing data → return error with `errorCode` / `fallbackMessage`
 */
export function transformResponse<TRaw extends { success: boolean; message?: string }, TOut>(
  response: ApiResponse<TRaw>,
  extractData: (raw: TRaw) => TOut | null,
  errorCode: string,
  fallbackMessage: string
): ApiResponse<TOut> {
  if (response.error) {
    return { data: null, error: response.error }
  }

  if (response.data) {
    const extracted = extractData(response.data)
    if (extracted) {
      return { data: extracted, error: null }
    }

    if (!response.data.success) {
      return {
        data: null,
        error: {
          message: response.data.message ?? fallbackMessage,
          code: errorCode,
        },
      }
    }
  }

  return {
    data: null,
    error: { message: fallbackMessage, code: errorCode },
  }
}
